import React, { useState } from "react";
import { Select, Tabs } from "antd";
import type { TabsProps } from "antd";
import { useScrollPin } from "../../../hooks/useScrollPin";

interface SubTab {
  key: string;
  label: string;
  content: React.ReactNode;
}

interface MainTab {
  key: string;
  label: string;
  subTabs: SubTab[];
  className?: string;
}

interface DynamicTabsProps {
  mainTabs: MainTab[];
  defaultActiveTab?: string;
  defaultSubTab?: string;
}

// Category row (e.g. Company Profile / Profitability / Dividend Ratios)
// stays a Tabs strip — few enough items to scan at a glance. The metric
// row underneath it (EPS Current Vs Past, Gross Net Revenue, ... — often
// a dozen+ items) used to be a second row of pill tabs, which both read
// as visual clutter stacked under the category row and silently
// truncated extra items behind antd's own "..." overflow dropdown. A
// single Select reads as one control instead of a row of buttons and
// never truncates: every metric is always in the list.
export const DynamicTabs: React.FC<DynamicTabsProps> = ({
  mainTabs,
  defaultActiveTab = "1",
  defaultSubTab = "1",
}) => {
  const [activeMainTab, setActiveMainTab] = useState<string>(defaultActiveTab);
  const [activeSubTabs, setActiveSubTabs] = useState<Record<string, string>>({
    [defaultActiveTab]: defaultSubTab,
  });
  const { containerRef: rootRef, pinScroll } = useScrollPin<HTMLDivElement>();

  const handleMainTabChange = (key: string) => {
    setActiveMainTab(key);
    if (!activeSubTabs[key]) {
      setActiveSubTabs((prev) => ({
        ...prev,
        [key]: mainTabs.find((tab) => tab.key === key)?.subTabs[0]?.key || "1",
      }));
    }
  };

  const handleSubTabChange = (mainTabKey: string, subTabKey: string) => {
    // Picking a metric remounts the chart below (see the `key` on the
    // content div) and antd returns focus to the Select once its
    // dropdown closes — both nudge the scroll container back up. Some
    // metrics also fetch their chart data on demand and render a much
    // shorter placeholder first, so the container keeps changing height
    // as the real chart loads in. pinScroll() holds the scroll position
    // through both instead of letting the user get bounced away from
    // the chart they're on.
    pinScroll();
    setActiveSubTabs((prev) => ({
      ...prev,
      [mainTabKey]: subTabKey,
    }));
  };

  const mainTabItems: TabsProps["items"] = mainTabs.map((tab) => {
    const activeSubTabKey = activeSubTabs[tab.key] || tab.subTabs[0]?.key;
    const activeSubTab = tab.subTabs.find(
      (subTab) => subTab.key === activeSubTabKey,
    );

    return {
      key: tab.key,
      label: `${tab.label}`,
      className: tab.className,
      children: (
        <div className="mt-2">
          {tab.subTabs.length > 1 && (
            <Select
              value={activeSubTabKey}
              onChange={(key) => handleSubTabChange(tab.key, key)}
              className="vi-cmp-metric-select mb-4"
              popupClassName="vi-cmp-select-dropdown"
              style={{ minWidth: 240 }}
              options={tab.subTabs.map((subTab) => ({
                value: subTab.key,
                label: subTab.label,
              }))}
            />
          )}
          {/* key forces a fresh mount per metric — the content is a
              DynamicTab whose own data-fetch effect depends on
              [primaryStock]/[selectedStocks], not on which snapshot it
              was given, so swapping `content` in place without a key
              just updates props on the same instance and it never
              re-fetches for the newly picked metric. */}
          <div key={activeSubTabKey}>{activeSubTab?.content}</div>
        </div>
      ),
    };
  });

  return (
    <div className="w-full" ref={rootRef}>
      <Tabs
        activeKey={activeMainTab}
        onChange={handleMainTabChange}
        items={mainTabItems}
        className="custom_tab vi-cmp-main-tabs"
      />
    </div>
  );
};
